import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { LockOutlined, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';
import { evaluatePassword } from '../../utils/passwordStrength';
import { messageFromApiBody, toUserErrorMessage } from '../../utils/apiError';

/**
 * Accept platform-provisioned firm admin invite — sets password via /api/auth/accept-tenant-invite.
 */
const AcceptTenantInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [firmName, setFirmName] = useState('');
  const navigate = useNavigate();

  const strength = evaluatePassword(password);
  const canSubmit =
    Boolean(token) &&
    strength.valid &&
    password === confirmPassword &&
    !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/accept-tenant-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(messageFromApiBody(json, 'Failed to set password'));
      }
      setFirmName(json?.data?.firmName || '');
      setDone(true);
      toast.success('Password set — you can sign in');
    } catch (err) {
      setError(toUserErrorMessage(err, 'Failed to set password'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'grey.50',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Button
          startIcon={<ArrowBack />}
          component={Link}
          to="/login"
          sx={{ mb: 2 }}
        >
          Back to login
        </Button>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <LockOutlined color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" fontWeight={600}>
            Set firm admin password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {firmName
              ? `Complete setup for ${firmName}`
              : 'Choose a password for your ArgisLegal administrator account'}
          </Typography>
        </Box>

        {!token && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Missing invite token. Open the link from your invitation email.
          </Alert>
        )}

        {done ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Password saved. You can now{' '}
            <MuiLink component={Link} to="/login" underline="hover">
              sign in to the dashboard
            </MuiLink>
            .
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordStrengthMeter password={password} />
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              error={Boolean(confirmPassword) && password !== confirmPassword}
              helperText={
                confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ' '
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!canSubmit}
              sx={{ mt: 1 }}
            >
              {isLoading ? <CircularProgress size={22} /> : 'Activate account'}
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')}>
              Go to login
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AcceptTenantInvitePage;
